import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { BackgroundJobsService } from 'src/app/services/background-jobs/background-jobs.service';
import {
  UploadJobStatus,
  UploadJobStatusResponse,
} from '../bulk-upload-preview/bulk-upload-preview.types';

export type BulkUploadMode = 'preview' | 'upload' | 'commit';

export interface BulkUploadState {
  active: boolean; // a job exists (pill visible)
  processing: boolean; // timers still running
  minimized: boolean; // pill vs in-modal view
  completed: boolean;
  failed: boolean;
  mode: BulkUploadMode;
  fileName: string;
  categoryName: string;
  /** Human-friendly category label from the API (e.g. "Cars") */
  categoryDisplayName: string;
  categoryId: number;
  percent: number;
  phaseIndex: number;
  elapsedSeconds: number;
  logs: string[];
  result: any | null;
  error: any | null;
  message: string;
  /** The backend job ID for the current upload */
  jobId: string | number | null;
}

const INITIAL_STATE: BulkUploadState = {
  active: false,
  processing: false,
  minimized: false,
  completed: false,
  failed: false,
  mode: 'preview',
  fileName: '',
  categoryName: '',
  categoryDisplayName: '',
  categoryId: 0,
  percent: 0,
  phaseIndex: 0,
  elapsedSeconds: 0,
  logs: [],
  result: null,
  error: null,
  message: '',
  jobId: null,
};

/** Map backend STAGE values to a phase index (0-based) for the progress indicator */
const STAGE_PHASE_MAP: Record<string, number> = {
  queued: 0,
  validating_template: 1,
  ai_converting: 2,
  validating_output: 3,
  preview_ready: 4,
  committing: 3,
  committed: 4,
  // Legacy/fallback stage values
  pending: 0,
  in_progress: 2,
  processing: 2,
  validating: 3,
  validated: 4,
  // Failures
  failed: -1,
  commit_failed: -1,
  validation_failed: -1,
};

/** Terminal statuses that stop polling */
const TERMINAL_STATUSES: Set<string> = new Set([
  'preview_ready',
  'committed',
  'failed',
  'commit_failed',
  'validation_failed',
  'validated',
  'cancelled',
]);

/**
 * Holds the bulk-upload processing state at app level so progress survives the
 * modal being minimized and the user navigating to other pages.
 *
 * Polling is sequential: the next status request is only sent AFTER the previous
 * response is received. No stacking of requests.
 */
@Injectable({ providedIn: 'root' })
export class BulkUploadProgressService {
  private rateManagerService = inject(RateManagerService);
  private backgroundJobs = inject(BackgroundJobsService);

  /** Minimum delay between polls (ms). Applied after response is received. */
  private readonly POLL_DELAY_MS = 3000;

  private state: BulkUploadState = { ...INITIAL_STATE };
  private state$ = new BehaviorSubject<BulkUploadState>(this.state);

  /**
   * Emits whenever a job reaches a terminal state (committed, preview ready,
   * or failed) so listeners like the job status bar can refresh their
   * in-progress and list views from the backend.
   */
  private jobsChanged$ = new Subject<void>();
  readonly jobsChanged: Observable<void> = this.jobsChanged$.asObservable();

  private elapsedTimer?: ReturnType<typeof setInterval>;
  private pollActive = false;
  private destroyed = false;
  /** Internal tracking ID for the BackgroundJobsService registration */
  private bgJobId: string | null = null;

  readonly changes: Observable<BulkUploadState> = this.state$.asObservable();

  get snapshot(): BulkUploadState {
    return this.state;
  }

  phases(mode: BulkUploadMode = this.state.mode) {
    if (mode === 'commit') {
      return [
        { label: 'Queued', description: 'Your request is in the queue' },
        { label: 'Validating', description: 'Checking data integrity' },
        { label: 'Converting', description: 'Processing rate data' },
        {
          label: 'Committing',
          description: 'Saving your rates to the database',
        },
        { label: 'Complete', description: 'Rates committed successfully' },
      ];
    }

    return [
      { label: 'Queued', description: 'Your file is in the queue' },
      {
        label: 'Validating Template',
        description: 'Checking file format and structure',
      },
      {
        label: 'AI Converting',
        description: 'Converting and parsing your data',
      },
      {
        label: 'Validating Output',
        description: 'Checking data quality and schema',
      },
      { label: 'Preview Ready', description: 'Your data is ready for review' },
    ];
  }

  // ─── Polling Mode (Sequential Await-Then-Poll) ──────────────────────────────

  /**
   * Starts a polling-based upload job.
   * 1. Uploads the file via the provided request
   * 2. Extracts the job_id from the response
   * 3. Sequentially polls GET /upload-jobs/{jobId} until terminal status
   *
   * Each poll request is only sent AFTER the previous one completes.
   */
  async startWithPolling(params: {
    mode: BulkUploadMode;
    fileName: string;
    categoryName?: string;
    categoryId?: number;
    uploadRequest: () => Observable<any>;
  }): Promise<any> {
    this.stopAll();
    this.destroyed = false;
    this.patch({
      ...INITIAL_STATE,
      active: true,
      processing: true,
      mode: params.mode,
      fileName: params.fileName,
      categoryName: params.categoryName || '',
      categoryId: params.categoryId || 0,
      logs: ['Uploading file...'],
    });

    this.startElapsedTimer();

    // Step 1: Upload the file
    let uploadResponse: any;
    try {
      uploadResponse = await firstValueFrom(params.uploadRequest());
    } catch (err) {
      return this.handleFailure(err);
    }

    const jobId =
      uploadResponse?.job_id ||
      uploadResponse?.data?.job_id ||
      uploadResponse?.id;
    if (!jobId) {
      // Fallback: if no job_id, treat the response as immediate result (backward compat)
      return this.handleSuccess(uploadResponse);
    }

    this.patch({
      jobId,
      percent: 5,
      phaseIndex: 0,
      logs: [
        ...this.state.logs,
        `${this.elapsedDisplay()} — Job created (ID: ${jobId})`,
      ],
    });

    // Step 2: Sequential poll until done
    return this.pollSequentially(jobId);
  }

  /**
   * Sequential polling loop. Only sends the next request after
   * the previous one has responded and a minimum delay has passed.
   */
  private async pollSequentially(jobId: string | number): Promise<any> {
    this.pollActive = true;

    while (this.pollActive && !this.destroyed) {
      const startTime = Date.now();

      try {
        const response = await firstValueFrom(
          this.rateManagerService.getUploadJobStatus(jobId),
        );
        const job: UploadJobStatusResponse = response?.data || response;
        this.updateFromJobStatus(job);

        const status = job.status;

        if (TERMINAL_STATUSES.has(status)) {
          this.pollActive = false;

          if (
            status === 'failed' ||
            status === 'commit_failed' ||
            status === 'validation_failed'
          ) {
            const err = {
              message: job.error_message || 'Processing failed',
              error: job,
            };
            this.handleFailure(err);
            throw err;
          }

          // Success (preview_ready, committed, validated)
          this.handleSuccess(job);
          return job;
        }
      } catch (err: any) {
        // If it's our own thrown error from handleFailure, re-throw
        if (err?.error?.status && TERMINAL_STATUSES.has(err.error.status)) {
          throw err;
        }
        // Network/unexpected error — stop polling
        this.pollActive = false;
        this.handleFailure(err);
        throw err;
      }

      // Wait at least POLL_DELAY_MS from the start of this iteration
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, this.POLL_DELAY_MS - elapsed);
      if (remainingDelay > 0) {
        await this.delay(remainingDelay);
      }
    }

    // If we exit the loop due to destroyed flag
    return null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private updateFromJobStatus(job: UploadJobStatusResponse): void {
    // Use `stage` field for phase mapping (e.g. "validating_template", "ai_converting")
    // Fall back to `status` if stage is not present
    const stage = job.stage || job.status;
    const phaseIndex = STAGE_PHASE_MAP[stage] ?? this.state.phaseIndex;
    const percent =
      job.progress_percent ??
      job.progress ??
      this.estimatePercentFromStage(stage);

    const newLogs = [...this.state.logs];
    if (phaseIndex !== this.state.phaseIndex && phaseIndex >= 0) {
      const label = this.phases()[phaseIndex]?.label || stage;
      newLogs.push(`${this.elapsedDisplay()} — ${label}`);
    }

    // Extract category info from job response (more dynamic than modal selection)
    const categoryDisplayName =
      job.category_display_name || this.state.categoryDisplayName;
    const categoryName =
      job.category_name || job.rate_category_name || this.state.categoryName;
    const categoryId = job.rate_category_id || this.state.categoryId;

    this.patch({
      percent: Math.max(this.state.percent, percent),
      phaseIndex: phaseIndex >= 0 ? phaseIndex : this.state.phaseIndex,
      categoryDisplayName,
      categoryName,
      categoryId,
      logs: newLogs,
    });
  }

  private estimatePercentFromStage(stage: string): number {
    switch (stage) {
      case 'queued':
      case 'pending':
        return 10;
      case 'validating_template':
        return 25;
      case 'ai_converting':
      case 'in_progress':
      case 'processing':
        return 50;
      case 'validating_output':
      case 'validating':
        return 75;
      case 'preview_ready':
      case 'validated':
        return 100;
      case 'committing':
        return 70;
      case 'committed':
        return 100;
      case 'failed':
      case 'commit_failed':
      case 'validation_failed':
        return this.state.percent;
      default:
        return this.state.percent;
    }
  }

  // ─── Request Mode (Legacy / Commit) ─────────────────────────────────────────

  /**
   * Starts a single-request job (used for commit step).
   * Keeps the old behavior: awaits one Observable, shows progress animation.
   */
  async start(params: {
    mode: BulkUploadMode;
    fileName: string;
    request: () => Observable<any>;
  }): Promise<any> {
    this.stopAll();
    this.destroyed = false;
    this.patch({
      ...INITIAL_STATE,
      active: true,
      processing: true,
      mode: params.mode,
      fileName: params.fileName,
      jobId: this.state.jobId, // preserve jobId if committing same job
      logs: [
        params.mode === 'commit'
          ? 'Committing rates...'
          : 'File queued for processing',
      ],
    });

    this.startElapsedTimer();
    this.startProgressAnimation();

    let result: any = null;
    let error: any = null;
    try {
      result = await firstValueFrom(params.request());
    } catch (err) {
      error = err;
    }

    this.stopAll();

    if (error) {
      return this.handleFailure(error);
    }
    return this.handleSuccess(result);
  }

  // ─── Common Handlers ────────────────────────────────────────────────────────

  private handleSuccess(result: any): any {
    this.stopAll();
    this.patch({
      processing: false,
      completed: true,
      percent: 100,
      phaseIndex: this.phases().length - 1,
      result,
      message: result?.message || '',
      logs: [
        ...this.state.logs,
        `${this.elapsedDisplay()} — Processing complete`,
      ],
    });

    const successText =
      this.state.mode === 'commit'
        ? result?.message || 'Rates committed successfully.'
        : this.state.mode === 'upload'
          ? result?.message || 'Bulk rates uploaded successfully.'
          : 'File validated. Review the results to continue.';
    AlertUtil.toast(
      successText,
      'success',
      this.state.mode === 'commit'
        ? 'Commit complete'
        : this.state.mode === 'upload'
          ? 'Upload complete'
          : 'Validation complete',
    );

    // Notify listeners (e.g. job status bar) that job state changed so they
    // can refresh their in-progress and list views from the backend.
    this.jobsChanged$.next();
    return result;
  }

  private handleFailure(err: any): never {
    this.stopAll();
    this.patch({
      processing: false,
      failed: true,
      percent: this.state.percent,
      error: err,
      logs: [
        ...this.state.logs,
        `${this.elapsedDisplay()} — Processing failed`,
      ],
    });
    AlertUtil.toast(this.errorMessage(err), 'error', 'Processing failed');

    // Notify listeners so the job status bar reflects the failed state.
    this.jobsChanged$.next();
    throw err;
  }

  private errorMessage(err: any): string {
    // Handle 413 Payload Too Large
    if (err?.status === 413) {
      return 'The uploaded file is too large. Please reduce the number of rows or split it into smaller files and try again.';
    }

    const body = err?.error;

    // Handle nested error object: error.error can be a string or { error: "..." }
    if (body?.error) {
      if (typeof body.error === 'string') return body.error;
      if (
        typeof body.error === 'object' &&
        typeof body.error.error === 'string'
      )
        return body.error.error;
    }

    // Fallback to message field
    if (typeof body?.message === 'string') return body.message;
    if (typeof err?.message === 'string') return err.message;

    return 'Failed to process file.';
  }

  // ─── UI Controls ────────────────────────────────────────────────────────────

  minimize() {
    this.patch({ minimized: true });
  }

  expand() {
    this.patch({ minimized: false });
  }

  /** Clears the job entirely (hides the pill). */
  dismiss() {
    this.destroyed = true;
    this.stopAll();
    if (this.bgJobId) {
      this.backgroundJobs.dismissJob(this.bgJobId);
      this.bgJobId = null;
    }
    this.patch({ ...INITIAL_STATE });
  }

  elapsedDisplay(seconds: number = this.state.elapsedSeconds): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // ─── Timers ─────────────────────────────────────────────────────────────────

  private startElapsedTimer(): void {
    this.elapsedTimer = setInterval(() => {
      this.patch({ elapsedSeconds: this.state.elapsedSeconds + 1 });
    }, 1000);
  }

  /** Legacy: animated progress for single-request mode (commit) */
  private animationTimer?: ReturnType<typeof setInterval>;
  private startedAt = 0;

  private startProgressAnimation(): void {
    const ANIMATION_DURATION_MS = 30000;
    this.startedAt = Date.now();
    this.animationTimer = setInterval(() => {
      const ratio = (Date.now() - this.startedAt) / ANIMATION_DURATION_MS;
      const percent = Math.min(95, Math.round(ratio * 100));
      this.patch({ percent });
      this.syncPhase();
    }, 300);
  }

  private syncPhase() {
    const p = this.state.percent;
    const phaseIndex = p < 15 ? 0 : p < 35 ? 1 : p < 55 ? 2 : p < 80 ? 3 : 4;
    if (phaseIndex !== this.state.phaseIndex) {
      const label = this.phases()[phaseIndex]?.label || 'Processing';
      this.patch({
        phaseIndex,
        logs: [
          ...this.state.logs,
          `${this.elapsedDisplay()} — ${label} started`,
        ],
      });
    }
  }

  private stopAll() {
    this.pollActive = false;
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = undefined;
    }
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = undefined;
    }
  }

  private patch(partial: Partial<BulkUploadState>) {
    this.state = { ...this.state, ...partial };
    this.state$.next(this.state);
    this.syncToBackgroundJobs();
  }

  /** Keep the generic BackgroundJobsService in sync with this service's state */
  private syncToBackgroundJobs(): void {
    if (!this.state.active) {
      // If dismissed, remove from background jobs
      if (this.bgJobId) {
        this.backgroundJobs.dismissJob(this.bgJobId);
        this.bgJobId = null;
      }
      return;
    }

    if (!this.bgJobId) {
      this.bgJobId = `bulk-upload-${Date.now()}`;
    }

    const phaseLabel =
      this.phases()[this.state.phaseIndex]?.label || 'Processing';

    if (this.state.completed) {
      this.backgroundJobs.upsertJob({
        id: this.bgJobId,
        type: 'Bulk Upload',
        label: this.state.fileName,
        state: 'completed',
        progress: 100,
        phaseLabel:
          this.state.mode === 'commit' ? 'Committed' : 'Preview Ready',
        elapsedSeconds: this.state.elapsedSeconds,
        icon: 'bi-file-earmark-check',
        message: this.state.message || undefined,
      });
    } else if (this.state.failed) {
      this.backgroundJobs.upsertJob({
        id: this.bgJobId,
        type: 'Bulk Upload',
        label: this.state.fileName,
        state: 'failed',
        progress: this.state.percent,
        phaseLabel: 'Failed',
        elapsedSeconds: this.state.elapsedSeconds,
        icon: 'bi-file-earmark-x',
        message: this.errorMessage(this.state.error),
      });
    } else {
      this.backgroundJobs.upsertJob({
        id: this.bgJobId,
        type: 'Bulk Upload',
        label: this.state.fileName,
        state: 'processing',
        progress: this.state.percent,
        phaseLabel,
        elapsedSeconds: this.state.elapsedSeconds,
        icon: 'bi-file-earmark-arrow-up',
      });
    }
  }
}
